import React, { useState, useEffect } from 'react';
import { useFlowBuilderStore } from '../../../stores/flowBuilderStore';
import { api } from '../../../lib/api';
import { Input } from '../../ui';
import { Loader2 } from 'lucide-react';

interface TransferToQueueConfigProps {
  config: any;
  onChange: (key: string, value: any) => void;
}

export const TransferToQueueConfig: React.FC<TransferToQueueConfigProps> = ({ config, onChange }) => {
  const { connectionId } = useFlowBuilderStore();
  const [openLines, setOpenLines] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadOpenLines = async () => {
      if (!connectionId) return;
      setIsLoading(true);
      setError(null);
      try {
        const lines = await api.get<any[]>(`/bitrix/connection/${connectionId}/openlines`);
        if (Array.isArray(lines)) {
            setOpenLines(lines);
        } else if (lines && typeof lines === 'object') {
            setOpenLines(Object.values(lines));
        } else {
            setOpenLines([]);
        }
      } catch (error: any) {
        console.error('Failed to load open lines:', error);
        const msg = String(error?.message || error || '');
        // A dead OAuth token answers 'authorization_error: Unable to authorize
        // user' on EVERY method — it is not a missing Open Lines permission.
        // Blaming the scope here sends the client hunting the wrong setting.
        const isAuth =
          /invalid_grant|authorization_error|unable to authorize|no_auth_found|expired_token|401|403/i.test(msg) ||
          msg.includes('token');
        setError(isAuth ? 'token_expired' : msg || 'error');
      } finally {
        setIsLoading(false);
      }
    };

    loadOpenLines();
  }, [connectionId]);

  return (
    <div className="space-y-4">
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-xs text-blue-700 leading-relaxed">
        <strong>Como funciona:</strong> Transfere a conversa para uma fila de atendimento (Open Channel) do Bitrix24. A conversa será distribuída automaticamente entre os operadores disponíveis na fila selecionada. Opcionalmente, envia uma mensagem antes da transferência.
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Fila de Atendimento (Open Channel)
        </label>
        {isLoading ? (
            <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              Carregando filas...
            </div>
        ) : error ? (
          <div className="space-y-2">
              <p className="text-xs text-red-600 bg-red-50 p-2 rounded border border-red-100">
                {error === 'token_expired' ? (
                  <>
                    O Bitrix24 recusou o acesso desta conexão. Normalmente é o usuário que instalou o
                    aplicativo que foi desativado ou perdeu permissão. Peça para um administrador abrir
                    o iBot <strong>de dentro do Bitrix24</strong> (pelo ícone do aplicativo) — isso
                    reatribui a conexão e restaura o acesso.
                  </>
                ) : (
                  <>
                    Não foi possível carregar as filas do Bitrix24. Verifique se o aplicativo tem
                    permissão "Open Lines" (Open Channels).
                    <span className="mt-1 block opacity-80">{error}</span>
                  </>
                )}
              </p>
              <Input 
                placeholder="ID da Fila (chatXXX ou número)"
                value={config.queueId || ''}
                onChange={(e) => onChange('queueId', e.target.value)}
              />
          </div>
        ) : (
          <select
            className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            value={config.queueId || ''}
            onChange={(e) => onChange('queueId', e.target.value)}
          >
            <option value="">Selecione uma fila</option>
            {openLines.map((line: any) => (
              <option key={line.ID} value={line.ID}>
                {line.LINE_NAME || `Fila ${line.ID}`}
              </option>
            ))}
          </select>
        )}
          <p className="text-xs text-slate-500 mt-1">
          O cliente será transferido para esta fila no Bitrix24.
        </p>
      </div>
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1">
          Mensagem de Transferência
        </label>
        <textarea
          className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          rows={2}
          placeholder="Transferindo para um atendente..."
          value={config.message || ''}
          onChange={(e) => onChange('message', e.target.value)}
        />
      </div>
    </div>
  );
};
